import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Alert,
  Typography,
  Stack,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../../app/api/cloudApi';
import { useAvailableActions } from '../../../hooks/useAvailableActions';

export default function GeneralSettingsTab({ device, deviceId, siteId, accountId, token, onRenamed }) {
  const { enqueueSnackbar } = useSnackbar();
  const { canEditSites } = useAvailableActions();

  const [name, setName] = useState(device?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setName(device?.name || '');
    setError(null);
  }, [device?.deviceID, device?.name]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      enqueueSnackbar('Device name is required', { variant: 'warning' });
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await cloudApi.renameFaceScannerDevice(token, siteId, accountId, deviceId, trimmedName);
      enqueueSnackbar('Device renamed successfully', { variant: 'success' });
      onRenamed?.(deviceId, trimmedName);
    } catch (err) {
      console.error('Error renaming device:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to rename device';
      setError(errorMessage);
      enqueueSnackbar(typeof errorMessage === 'string' ? errorMessage : 'Failed to rename device', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <Typography variant="h6">Device Information</Typography>

        <TextField
          fullWidth
          label="Device Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="A friendly name for this device, shown throughout the site settings pages"
          disabled={!canEditSites}
        />

        <TextField
          fullWidth
          label="Device ID"
          value={device?.deviceID ?? deviceId ?? ''}
          disabled
          helperText="Physical device identifier (read-only)"
        />

        <TextField
          fullWidth
          label="Model"
          value={device?.modelName || device?.type || 'N/A'}
          disabled
        />

        {canEditSites && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || (name.trim() || '') === (device?.name || '')}
              startIcon={saving ? undefined : <Icon icon="solar:diskette-bold" />}
            >
              {saving ? 'Saving...' : 'Save Name'}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
